import React, { useState, useEffect, useRef } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Box,
  VStack,
  Heading,
  Text,
  Button,
  Flex,
  useDisclosure,
  Textarea,
  Input,
} from "@chakra-ui/react";
import Image from "next/image";
import Link from "next/link";

import { Icon, IconProps, createIcon } from "@chakra-ui/icons";
import { FC, SVGProps } from "react";
interface EmptyTransactionIconProps extends IconProps {
  fill?: SVGProps<SVGGElement>["fill"];
  stroke?: SVGProps<SVGElement>["stroke"];
}
const OmnidIcon = createIcon({
  displayName: "OmnidIcon",
  viewBox: "0 0 512 512",
  path: (
    <>
      <path
        fill="currentColor"
        d="M262.2 498.2C92.8 486 91.4 43.9 253.1 13.8c169.6 9.7 169.1 460.3 9.1 484.4zM338.9 256c4.3-52.2-28.1-295.4-114.3-206.1-64.5 109.3-62.9 302.5 0 412.2 87.2 89.6 118.1-154.2 114.3-206.1z"
      />
      <path
        fill="currentColor"
        d="M501.3 314.7v6h-1.5v9c-50.5 112.8-453 116.7-484.4-9 12.2-132.7 460.5-132.8 484.4-6h1.5zM257.6 253c-68.3 1.1-140.6 8.5-203.1 37.6-28.2 22.8-31 31.4 0 54.2 53.6 46.9 451.9 59.3 427.2-37.6-47.5-49.8-157.3-51.9-224.1-54.2z"
      />
    </>
  ),
});

const App = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [emailButtonText, setEmailButtonText] = useState("Send");
  const emailText = useRef<HTMLTextAreaElement | null>(null);
  const emailEmail = useRef<HTMLInputElement | null>(null);
  const [sending, setSending] = useState<boolean>(false);

  const post = async () => {
    setSending(true);
    const value = emailText.current?.value;
    const email = emailEmail.current?.value;
    if (value && email) {
      let res = await fetch("/api/email", {
        method: "POST",
        body: JSON.stringify({
          text: value.trim(),
          userEmail: email.trim(),
        }),
      });
      let resp = await res.json();
      console.log(resp);

      if (resp["success"] == true) {
        setEmailButtonText("Sent!");
      }

      setSending(false);
    } else {
      setSending(false);
    }
  };

  return (
    <Flex w="100%" justifyContent="center" alignItems="center">
      <Flex
        w={{ base: "90%", md: "70%", lg: "60%" }}
        marginTop={20}
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <Text>Omnid App - Privacy Policy</Text>
        <Text mt={4} lineHeight="30px">
          <strong>1. Introduction</strong>
          <br />
          Welcome to Omnid App ("we", "our", "us"). We value your privacy and
          are committed to protecting your personal information. This Privacy
          Policy explains how we collect, use, disclose, and safeguard your
          information when you use our mobile application ("App"). Please read
          this privacy policy carefully. If you do not agree with the terms of
          this privacy policy, please do not access the app.
          <br />
          You can access this Privacy Policy anytime through the following URL:{" "}
          <a href="https://omnid.io/privacy-policy">Omnid App Privacy Policy</a>
          .
        </Text>
        <Text mt={4} lineHeight="30px">
          <strong>2. Information We Collect</strong>
          <br />
          2.1 <strong>Personal Information</strong> When you use our App, we may
          collect the following types of personal information:
          <ul>
            <li>Contact Information: Name, email address, and phone number.</li>
            <li>
              Authentication Data: Usernames, passwords, and other
              security-related information.
            </li>
            <li>Profile Information: Profile photo and preferences.</li>
          </ul>
          <br />
          2.2 <strong>Usage Data</strong> We may also collect information that
          your device sends whenever you visit our App ("Usage Data"). This
          Usage Data may include:
          <ul>
            <li>
              Device Information: Information about your device such as device
              type, operating system, and unique device identifiers.
            </li>
            <li>
              Log Information: Information about how you use our App, including
              access times, pages viewed, and IP address.
            </li>
          </ul>
          <br />
          2.3 <strong>Location Data</strong> We may use and store information
          about your location if you give us permission to do so ("Location
          Data"). We use this data to provide features of our service, to
          improve and customize our service.
          <br />
          2.4 <strong>Firebase Data</strong> Our App uses Firebase, a backend
          service provided by Google, for various functionalities including:
          <ul>
            <li>Analytics: To track app usage and performance.</li>
            <li>Authentication: To authenticate users.</li>
            <li>Cloud Messaging: To send push notifications.</li>
          </ul>
        </Text>
        <Text mt={4} lineHeight="30px">
          <strong>3. How We Use Your Information</strong>
          <br />
          We use the information we collect in the following ways:
          <ul>
            <li>
              To Provide and Maintain Our Service: Including providing you with
              customer support.
            </li>
            <li>
              To Improve Our Service: We analyze data to understand and improve
              our service.
            </li>
            <li>
              To Communicate with You: We may send you updates, security alerts,
              and other notifications.
            </li>
            <li>
              To Personalize Your Experience: Providing you with content and
              features that match your preferences.
            </li>
            <li>
              For Security and Fraud Prevention: To protect our service and
              users from fraud and other security issues.
            </li>
          </ul>
        </Text>
        <Text mt={4} lineHeight="30px">
          <strong>4. Sharing Your Information</strong>
          <br />
          We may share your information in the following circumstances:
          <ul>
            <li>
              <strong>With Service Providers:</strong> We may share information
              with third-party service providers who perform services on our
              behalf.
            </li>
            <li>
              <strong>For Legal Reasons:</strong> If we are required to do so by
              law or in response to valid requests by public authorities.
            </li>
            <li>
              <strong>Business Transfers:</strong> In connection with any
              merger, sale of company assets, financing, or acquisition of all
              or a portion of our business.
            </li>
          </ul>
        </Text>
        <Text mt={4} lineHeight="30px">
          <strong>5. Data Security</strong>
          <br />
          We use administrative, technical, and physical security measures to
          help protect your personal information. While we have taken reasonable
          steps to secure the personal information you provide to us, please be
          aware that no security measures are perfect or impenetrable, and no
          method of data transmission can be guaranteed against interception or
          misuse.
        </Text>
        <Text mt={4} lineHeight="30px">
          <strong>6. Data Retention</strong>
          <br />
          We will retain your information for as long as your account is active
          or as needed to provide you services, comply with our legal
          obligations, resolve disputes, and enforce our agreements.
        </Text>
        <Text mt={4} lineHeight="30px">
          <strong>7. Your Data Protection Rights</strong>
          <br />
          Depending on your location, you may have the following rights
          regarding your personal information:
          <ul>
            <li>
              <strong>Access:</strong> You can request a copy of your personal
              data.
            </li>
            <li>
              <strong>Correction:</strong> You can request to correct any
              inaccurate data.
            </li>
            <li>
              <strong>Deletion:</strong> You can request to delete your personal
              data.
            </li>
            <li>
              <strong>Objection:</strong> You can object to the processing of
              your personal data.
            </li>
            <li>
              <strong>Restriction:</strong> You can request to restrict the
              processing of your personal data.
            </li>
            <li>
              <strong>Portability:</strong> You can request to transfer your
              personal data to another entity.
            </li>
          </ul>
        </Text>
        <Text mt={4} lineHeight="30px">
          <strong>8. Children's Privacy</strong>
          <br />
          Our service does not address anyone under the age of 13. We do not
          knowingly collect personally identifiable information from children
          under 13. If we become aware that we have collected personal data from
          a child under 13 without verification of parental consent, we will
          take steps to remove that information from our servers.
        </Text>
        <Text mt={4} lineHeight="30px">
          <strong>9. Contact Information</strong>
          <br />
          If you have any questions or concerns about this Privacy Policy, you
          can contact us at{" "}
          <a href="mailto:privacy@omnid.io">privacy@omnid.io</a> or visit our
          website: <a href="https://www.omnid.io">Omnid App</a>.
        </Text>
        <Flex marginTop="300px"> </Flex>
      </Flex>
    </Flex>
  );
};

export default App;
